import { useCallback, useEffect, useRef, useState } from 'react';
import {
  digitalGain, exposureConstraints, focusConstraints, focusLockConstraints, lockedExposureConstraints, pointConstraints,
  readCapabilities, readSettings, tapPoint, torchConstraints, whiteBalanceConstraints, zoomConstraints, zoomForPinch,
} from '../../utils/camera/controls';
import { bracketConstraints } from '../../utils/camera/capture';

/** The video track playing in a <video>, or null. */
export function trackOf(video) {
  const stream = video?.srcObject;
  if (!stream || typeof stream.getVideoTracks !== 'function') return null;
  return stream.getVideoTracks()[0] || null;
}

const AUTO_FOCUS = Object.freeze({ manual: false, locked: false });
const AUTO_WB = Object.freeze({ manual: false, temperature: null });

/**
 * The live camera's adjustments (utils/camera/controls.js) while `live`:
 * what the camera in `videoRef` offers, read again each time it comes back
 * (it restarts when the page is hidden), and setters that change it. A
 * setting the camera refuses is reported in `refused` and changes nothing
 * else. Where the camera has no exposure control, `gain` is the digital
 * brightness for the preview (`previewFilter`) and the captured picture.
 * `getTrack` overrides where the track is found (the QR scanner's library
 * draws its own video).
 *
 * The Pro drawer's (2026-10-22): a focus lock, an exposure lock with its
 * shutter and ISO, the white balance, the zoom -- `onPinch` sets it from a
 * pinch on the picture, null where the camera has none -- the bracket's
 * steps (`applyBracket`, `restoreExposure`) and a Z-stack's (`applyFocus`,
 * `focusNow`, `holdFocus`, `restoreFocus`). Zoom changes go one at a time,
 * the latest wins, so a pinch never queues up a stale zoom. `adjusted` says
 * whether anything is set away from automatic. Nothing is stored: a camera
 * opened again starts on automatic.
 */
export function useCameraControls(videoRef, live, { getTrack = null } = {}) {
  const [caps, setCaps] = useState(null);
  const [exposure, setExposureValue] = useState(0);
  const [focus, setFocusState] = useState({ ...AUTO_FOCUS, distance: null });
  const focusRef = useRef(focus);
  const setFocusValue = useCallback((next) => {
    focusRef.current = next;
    setFocusState(next);
  }, []);
  const [torch, setTorchValue] = useState(false);
  // {exposureTime, iso} while the exposure is set by hand (locked), else null.
  const [manualExposure, setManualState] = useState(null);
  const manualRef = useRef(null);
  const setManual = useCallback((next) => {
    manualRef.current = next;
    setManualState(next);
  }, []);
  const [whiteBalance, setWbState] = useState(AUTO_WB);
  const wbRef = useRef(AUTO_WB);
  const setWb = useCallback((next) => {
    wbRef.current = next;
    setWbState(next);
  }, []);
  const [zoom, setZoomState] = useState(null);
  const zoomRef = useRef(null);
  const [refused, setRefused] = useState('');
  const trackRef = useRef(null);
  const capsRef = useRef(null);
  const exposureRef = useRef(0);
  // The zoom wanted next while one is still being applied, and whether one is.
  const zoomWanted = useRef(null);
  const zoomBusy = useRef(false);
  const pinchStart = useRef(null);

  useEffect(() => {
    if (!live) {
      trackRef.current = null;
      capsRef.current = null;
      setCaps(null);
      return;
    }
    const track = getTrack ? getTrack() : trackOf(videoRef.current);
    const read = readCapabilities(track);
    trackRef.current = track;
    capsRef.current = read;
    setCaps(read);
    exposureRef.current = read.exposure.value;
    setExposureValue(read.exposure.value);
    setFocusValue({ ...AUTO_FOCUS, distance: read.focusDistance ? read.focusDistance.value : null });
    setTorchValue(false);
    setManual(null);
    setWb({ manual: false, temperature: read.whiteBalance ? read.whiteBalance.temperature.value : null });
    zoomRef.current = read.zoom ? read.zoom.value : null;
    setZoomState(zoomRef.current);
    zoomWanted.current = null;
    setRefused('');
  }, [live, videoRef, getTrack, setFocusValue, setManual, setWb]);

  const apply = useCallback(async (constraints, what) => {
    const track = trackRef.current;
    if (!track || typeof track.applyConstraints !== 'function') return false;
    try {
      await track.applyConstraints(constraints);
      setRefused('');
      return true;
    } catch {
      setRefused(what);
      return false;
    }
  }, []);

  const setExposure = useCallback((value) => {
    exposureRef.current = value;
    setExposureValue(value);
    const c = capsRef.current;
    if (c && c.exposure.kind === 'camera') apply(exposureConstraints(value, c), 'exposure');
  }, [apply]);

  const setManualFocus = useCallback((manual) => {
    const c = capsRef.current;
    if (!c) return;
    const next = { ...focusRef.current, manual: !!manual && c.manualFocus, locked: false };
    setFocusValue(next);
    apply(focusConstraints(next, c), 'focus');
  }, [apply, setFocusValue]);

  const setFocusDistance = useCallback((distance) => {
    const c = capsRef.current;
    if (!c || !c.manualFocus) return;
    const next = { manual: true, locked: false, distance };
    setFocusValue(next);
    apply(focusConstraints(next, c), 'focus');
  }, [apply, setFocusValue]);

  /** Hold the focus where the automatic focus has it now (at its distance, where the camera says it), or let it go. */
  const setFocusLock = useCallback((on) => {
    const c = capsRef.current;
    if (!c || !c.focusLock) return;
    if (!on) {
      const next = { ...focusRef.current, manual: false, locked: false };
      setFocusValue(next);
      apply(focusConstraints(next, c), 'focus');
      return;
    }
    const now = readSettings(trackRef.current).focusDistance;
    const distance = Number.isFinite(now) ? now : focusRef.current.distance;
    setFocusValue({ manual: false, locked: true, distance });
    apply(focusLockConstraints(distance, c), 'focus');
  }, [apply, setFocusValue]);

  /**
   * A tap on the live picture: focus and expose there, where the camera offers it. Null for a tap off the
   * picture (a letterbox bar) or before the camera is live; else {x, y, supported, done}: where on the picture
   * (0-1), whether this camera takes a point at all (an iPhone's does not), and the promise of the camera's
   * answer (true when it took it) -- what the focus ring shows (useTapFocus.js). A tap lets a focus lock go,
   * and, where it meters there with the camera's automatic exposure, an exposure lock too: its sliders go
   * with it, so the drawer never shows a lock the camera no longer holds.
   */
  const focusAt = useCallback((event, fit = 'cover') => {
    const c = capsRef.current;
    const video = videoRef.current;
    if (!c || !video) return null;
    const point = tapPoint(event, video, fit);
    if (!point) return null;
    if (!c.pointOfInterest) return { ...point, supported: false, done: Promise.resolve(false) };
    setFocusValue({ ...focusRef.current, manual: false, locked: false });
    if (c.exposureMode && manualRef.current) setManual(null);
    return { ...point, supported: true, done: apply(pointConstraints(point.x, point.y, c), 'focus') };
  }, [apply, videoRef, setFocusValue, setManual]);

  const setTorch = useCallback((on) => {
    const c = capsRef.current;
    if (!c || !c.torch) return;
    setTorchValue(!!on);
    apply(torchConstraints(on), 'torch');
  }, [apply]);

  /** The exposure held as the automatic exposure has it now (AE-L), or automatic again with its compensation. */
  const setExposureLock = useCallback((on) => {
    const c = capsRef.current;
    if (!c || !c.exposureLock) return;
    if (!on) {
      setManual(null);
      apply(c.exposure.kind === 'camera' ? exposureConstraints(exposureRef.current, c)
        : { advanced: [{ exposureMode: c.exposureMode || 'continuous' }] }, 'exposure');
      return;
    }
    const now = readSettings(trackRef.current);
    const next = {
      exposureTime: Number.isFinite(now.exposureTime) ? now.exposureTime : c.exposureTime.value,
      iso: c.iso ? (Number.isFinite(now.iso) ? now.iso : c.iso.value) : null,
    };
    setManual(next);
    apply(lockedExposureConstraints(next, c), 'exposure');
  }, [apply, setManual]);

  const setManualExposure = useCallback((change) => {
    const c = capsRef.current;
    if (!c || !c.exposureLock || !manualRef.current) return;
    const next = { ...manualRef.current, ...change };
    setManual(next);
    apply(lockedExposureConstraints(next, c), 'exposure');
  }, [apply, setManual]);
  const setExposureTime = useCallback(exposureTime => setManualExposure({ exposureTime }), [setManualExposure]);
  const setIso = useCallback(iso => setManualExposure({ iso }), [setManualExposure]);

  const setManualWhiteBalance = useCallback((manual) => {
    const c = capsRef.current;
    if (!c || !c.whiteBalance) return;
    const next = { ...wbRef.current, manual: !!manual };
    setWb(next);
    apply(whiteBalanceConstraints(next, c), 'whiteBalance');
  }, [apply, setWb]);

  const setColorTemperature = useCallback((temperature) => {
    const c = capsRef.current;
    if (!c || !c.whiteBalance) return;
    const next = { manual: true, temperature };
    setWb(next);
    apply(whiteBalanceConstraints(next, c), 'whiteBalance');
  }, [apply, setWb]);

  const setZoom = useCallback((value) => {
    const c = capsRef.current;
    if (!c || !c.zoom || !Number.isFinite(value)) return;
    const next = Math.min(c.zoom.max, Math.max(c.zoom.min, value));
    zoomRef.current = next;
    setZoomState(next);
    zoomWanted.current = next;
    if (zoomBusy.current) return;
    zoomBusy.current = true;
    (async () => {
      while (zoomWanted.current !== null) {
        const wanted = zoomWanted.current;
        zoomWanted.current = null;
        await apply(zoomConstraints(wanted), 'zoom');
      }
      zoomBusy.current = false;
    })();
  }, [apply]);

  /** A pinch on the picture (hooks/useViewportGestures.js): the zoom where it began, times the spread. */
  const pinch = useCallback(({ phase, scale }) => {
    const c = capsRef.current;
    if (!c || !c.zoom) return;
    if (phase === 'start' || pinchStart.current === null) pinchStart.current = zoomRef.current ?? c.zoom.value;
    const wanted = zoomForPinch(pinchStart.current, scale, c.zoom);
    if (wanted !== null && wanted !== zoomRef.current) setZoom(wanted);
    if (phase === 'end') pinchStart.current = null;
  }, [setZoom]);

  /** One stop of a bracket (utils/camera/capture.js), from the exposure set now; true once the camera took it. */
  const applyBracket = useCallback((ev) => {
    const c = capsRef.current;
    const constraints = c && bracketConstraints(ev, { manual: manualRef.current, exposure: exposureRef.current }, c);
    return constraints ? apply(constraints, 'bracket') : Promise.resolve(false);
  }, [apply]);

  /** One step of a Z-stack (utils/zstack/engine.js): manual focus at `distance`, the panel's own state untouched;
   *  true once the camera took it. */
  const applyFocus = useCallback((distance) => {
    const c = capsRef.current;
    if (!c || !c.manualFocus) return Promise.resolve(false);
    return apply(focusConstraints({ manual: true, distance }, c), 'focus');
  }, [apply]);

  /** The focus distance the camera reports now, or null where it reports none (a Z-stack checks the lens moved). */
  const focusNow = useCallback(() => {
    const d = readSettings(trackRef.current).focusDistance;
    return Number.isFinite(d) ? d : null;
  }, []);

  /** A hand sweep's focus held where the automatic focus found it, so moving the phone or turning the knob
   *  moves the plane, not the lens; nothing where the person set a focus or the camera has no hold. `manual`
   *  false leaves out a manual hold (a camera that just refused one). True once held. */
  const holdFocus = useCallback(({ manual = true } = {}) => {
    const c = capsRef.current;
    const f = focusRef.current;
    if (!c || !c.focusLock || f.manual || f.locked || (c.focusLock === 'manual' && !manual)) return Promise.resolve(false);
    const now = readSettings(trackRef.current).focusDistance;
    return apply(focusLockConstraints(Number.isFinite(now) ? now : f.distance, c), 'focus');
  }, [apply]);

  /** The focus set before a Z-stack, back on the camera: automatic, locked or at its distance. */
  const restoreFocus = useCallback(() => {
    const c = capsRef.current;
    if (!c) return Promise.resolve(false);
    const f = focusRef.current;
    if (f.locked && c.focusLock) return apply(focusLockConstraints(f.distance, c), 'focus');
    if (f.manual && c.manualFocus) return apply(focusConstraints(f, c), 'focus');
    return c.autoFocusMode ? apply(focusConstraints({ manual: false }, c), 'focus') : Promise.resolve(true);
  }, [apply]);

  /** The exposure set before a bracket, back on the camera. */
  const restoreExposure = useCallback(() => {
    const c = capsRef.current;
    if (!c) return Promise.resolve(false);
    if (manualRef.current && c.exposureLock) return apply(lockedExposureConstraints(manualRef.current, c), 'exposure');
    if (c.exposure.kind === 'camera') return apply(exposureConstraints(exposureRef.current, c), 'exposure');
    return Promise.resolve(true);
  }, [apply]);

  /** Everything back to the camera's own: exposure 0 and automatic, automatic focus and white balance, the
   *  light off, the zoom it started at. */
  const reset = useCallback(() => {
    const c = capsRef.current;
    if (!c) return;
    const wasManual = !!manualRef.current;
    exposureRef.current = 0;
    setExposureValue(0);
    setTorchValue(false);
    setManual(null);
    setFocusValue({ ...AUTO_FOCUS, distance: c.focusDistance ? c.focusDistance.value : null });
    if (c.exposure.kind === 'camera') apply(exposureConstraints(0, c), 'exposure');
    else if (wasManual) apply({ advanced: [{ exposureMode: c.exposureMode || 'continuous' }] }, 'exposure');
    if (c.autoFocusMode) apply(focusConstraints({ manual: false }, c), 'focus');
    if (c.torch) apply(torchConstraints(false), 'torch');
    if (c.whiteBalance && wbRef.current.manual) {
      setWb({ ...wbRef.current, manual: false });
      apply(whiteBalanceConstraints({ manual: false }, c), 'whiteBalance');
    }
    if (c.zoom && zoomRef.current !== c.zoom.value) setZoom(c.zoom.value);
  }, [apply, setFocusValue, setManual, setWb, setZoom]);

  const gain = caps && caps.exposure.kind === 'digital' ? digitalGain(exposure) : 1;
  const adjusted = !!caps && (exposure !== 0 || torch || focus.manual || focus.locked || !!manualExposure
    || whiteBalance.manual || (!!caps.zoom && zoom !== caps.zoom.value));
  return {
    caps, exposure, focus, torch, refused, gain, manualExposure, whiteBalance, zoom, adjusted,
    previewFilter: gain !== 1 ? `brightness(${gain})` : undefined,
    setExposure, setManualFocus, setFocusDistance, setFocusLock, focusAt, setTorch, reset,
    setExposureLock, setExposureTime, setIso, setManualWhiteBalance, setColorTemperature, setZoom,
    onPinch: caps?.zoom ? pinch : null,
    applyBracket, restoreExposure, applyFocus, restoreFocus, focusNow, holdFocus,
    // What the camera calls itself, for a Z-stack's manifest.
    trackLabel: () => String(trackRef.current?.label || ''),
  };
}
